import { nextTurnstileToken, turnstileHeaders } from '@components/Turnstile/Turnstile';
import { GeneticCounselingRequest, IFinancialAssistanceForm } from '@customTypes/requisition';
import { FamilyProfile, IUserConsent } from '@customTypes/user';
import axios from '@hooks/useAxios';

// Extend Axios types to include custom properties
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressGlobalErrorToast?: boolean;
    skipResponseInterceptor?: boolean;
  }
}

// Type definitions for API payloads and responses
export interface PaymentDetails {
  charge_date: string | null;
  payment_status: string | null;
  total_cost: number | null;
}

interface ConsentResponse {
  consents: Array<IUserConsent>;
}

interface UserProfile {
  identifiers: {
    individual_id: string;
  };
}

export interface UserInfo {
  name?: {
    first?: string;
    last?: string;
    middle?: string;
  };
  contact_info?: Array<{
    email?: string;
    phone?: string;
    address?: {
      street_address?: string;
      city?: string;
      region?: string;
      postal_code?: string;
    };
  }>;
  demographics?: {
    sex?: string;
    dob?: string;
  };
  campaign?: {
    campaign_code: string;
    name: string;
    description?: string;
    is_active: boolean;
    external_member_id?: string;
    logo_url?: string;
  };
  fvt_referral_uuid?: string;
  family_profiles?: FamilyProfile[];
}

interface TestRequestsResponse {
  results: Array<{
    requisition_id: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

// Matches the API's CuresRelease request body; the order is identified by requisition.
interface CuresActReleaseRequest {
  requisition_id: string;
}

interface GeneticCounselingDetails {
  results: GeneticCounselingRequest[];
}

interface FinancialAssistanceResponse {
  message: string;
  [key: string]: unknown;
}

export interface PartnershipProduct {
  product_id: string;
  name: string;
  product_family_name?: string | null;
  product_test_name?: string | null;
  product_test_type?: string | null;
  product_test_subclass?: string | null;
  description: string;
  report_type: string;
  sort_priority?: number;
}

export interface CampaignLandingResponse {
  campaign_code: string;
  logo_url: string;
  name: string;
  products: PartnershipProduct[];
}

axios.defaults.validateStatus = function (status: number): boolean {
  return status < 400; // Resolve only if the status code is less than 400
};

export async function fetchPaymentDetails(requisitionId: string): Promise<PaymentDetails> {
  return axios
    .get(`/payment/${requisitionId}`, {
      suppressGlobalErrorToast: true,
    })
    .then(res => res.data);
}

export async function fetchCampaignLandingBySlug(slug: string): Promise<CampaignLandingResponse> {
  return axios.get(`/patient-portal/partnerships/${slug}`, { suppressGlobalErrorToast: true }).then(res => res.data);
}

export async function fetchAllConsents(
  payload: string[],
  { suppressErrorToast = false } = {},
): Promise<IUserConsent[]> {
  return axios
    .get(`/consent?consents=${payload}`, { suppressGlobalErrorToast: suppressErrorToast })
    .then(res => res.data);
}

export async function fetchUsersConsents(userId: string): Promise<ConsentResponse> {
  return axios.get(`/patient/${userId}/consent`).then(res => res.data);
}

export async function fetchUserID(): Promise<string> {
  return axios.get('/profile').then((res: { data: UserProfile }) => res.data.identifiers.individual_id);
}

export async function fetchUserInfo(userId: string): Promise<UserInfo> {
  return axios.get(`/patient/${userId}`).then(res => res.data);
}

export async function updateUserConsent(userId: string, consents: Record<string, unknown>): Promise<unknown> {
  return axios.patch(`/patient/${userId}/consent`, consents).then(res => res.data);
}

export async function fetchUserTestRequests(suppressErrorToast = false): Promise<TestRequestsResponse> {
  return axios.get('/requisition', { suppressGlobalErrorToast: suppressErrorToast }).then(res => res.data.results);
}

export async function fetchRequisitionDetailsById(
  requisitionId: string,
  options?: { suppressErrorToast?: boolean },
): Promise<unknown> {
  return axios
    .get(`/requisition/${requisitionId}`, options?.suppressErrorToast ? { suppressGlobalErrorToast: true } : undefined)
    .then(res => {
      res.data.requisition_id = requisitionId;
      return res.data;
    });
}

export async function makeCuresActRelease(requestPayload: CuresActReleaseRequest): Promise<unknown> {
  return axios.post('/cures-release', requestPayload).then(res => res.data);
}

/**
 * Post-test counseling for `individualId`. A guardian may pass a minor dependent's
 * identifier; the API answers 404 when the caller is neither the patient nor an
 * active guardian of that individual.
 */
export async function fetchGeneticCounselingDetails(
  individualId: string,
  { suppressErrorToast = false } = {},
): Promise<GeneticCounselingRequest | null> {
  return axios
    .get(`/patient/${individualId}/genetic-counseling`, { suppressGlobalErrorToast: suppressErrorToast })
    .then((res: { data: GeneticCounselingDetails }) => {
      const resultsLength = res.data.results.length;
      // we want the last (most recent) element
      return resultsLength ? res.data.results[resultsLength - 1] : null;
    });
}

/**
 *
 * @param payload FinancialAssistanceForm payload
 * @returns
 */
export const submitFinancialAssistance = async (
  payload: IFinancialAssistanceForm,
): Promise<FinancialAssistanceResponse> => {
  const turnstileToken = await nextTurnstileToken();
  return axios
    .post('/financial-assistance', payload, { headers: turnstileHeaders(turnstileToken) })
    .then(res => res.data);
};

// Share Results types and actions
interface ShareResultsPayload {
  email: string;
  name?: string;
}

interface ShareResultsResponse {
  message?: string;
  name?: string;
  email: string;
  created_at: string;
  is_up_to_date: boolean;
  shared_identifiers: string[];
  [key: string]: unknown;
}

export const shareResults = async (payload: ShareResultsPayload): Promise<ShareResultsResponse> => {
  return axios.post('/patient-portal/share-history', payload).then(res => res.data);
};

export const fetchShareHistory = async (): Promise<ShareResultsResponse[]> => {
  return axios
    .get<ShareResultsResponse[]>('/patient-portal/share-history', {
      params: {
        limit: 10,
        offset: 0,
      },
    })
    .then(res => res.data);
};

interface SendUpdatedResultsPayload {
  recipient_email: string;
}

export const sendUpdatedResults = async (payload: SendUpdatedResultsPayload): Promise<ShareResultsResponse> => {
  return axios.post('/patient/results/share/update', payload).then(res => res.data);
};
